import type {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsFlagLevel,
  OrganLookupCode,
} from '@repo/types';
import { AgeGroup, getAgeGroup, getAgeInYears } from '@repo/utils';

import adultBody from '#assets/images/gutBodyConnections/bodies/adult.webp';
import babyToddlerBody from '#assets/images/gutBodyConnections/bodies/baby-toddler.webp';
import childBody from '#assets/images/gutBodyConnections/bodies/child.webp';
import { HIGH_QUALITY_ARTWORK } from './high-quality';
import { LOW_QUALITY_ARTWORK } from './low-quality';
import type { GutBodyConnectionsArtwork, OrganArtworkSet } from './types';

export { GUT_WITH_UTERUS } from './high-quality';
export type { GutBodyConnectionsArtwork, OrganArtworkSet } from './types';

/** `low` is the PDF's downscaled set; everywhere else draws `high`. */
export type OrganArtworkQuality = 'high' | 'low';

const ARTWORK_SETS: Record<OrganArtworkQuality, OrganArtworkSet> = {
  high: HIGH_QUALITY_ARTWORK,
  low: LOW_QUALITY_ARTWORK,
};

/** Keyed on `organs.lookup_code`, so a key that drifts from the DB won't compile. */
type ArtworkByOrgan = Partial<
  Record<OrganLookupCode, GutBodyConnectionsArtwork>
>;

const ageInvariantOrgans = (set: OrganArtworkSet): ArtworkByOrgan => ({
  brain: set.brain,
  skin: set.skin,
});

/** Drawn for adults only. Identical for pregnant and non-pregnant. */
const adultOnlyOrgans = (set: OrganArtworkSet): ArtworkByOrgan => ({
  endocrine: set.endocrine,
  heart: set.heart,
  kidney: set.kidney,
  liver: set.liver,
  metabolic: set.metabolic,
  muscle: set.muscle,
});

/**
 * Organs the design file draws as half of a symmetric shape, cut at the midline,
 * so one drawing is placed twice and the second copy flipped. The parts are the
 * CSS keys; the first carries the system's dot, the second hides its own.
 */
const MIRRORED_PAIRS: Partial<Record<OrganLookupCode, [string, string]>> = {
  immune: ['immune1', 'immune2'],
  kidney: ['kidney1', 'kidney2'],
  metabolic: ['metabolic1', 'metabolic2'],
};

const organsByAgeGroup = (
  set: OrganArtworkSet,
): Partial<
  Record<OrganLookupCode, Record<AgeGroup, GutBodyConnectionsArtwork>>
> => ({
  immune: {
    [AgeGroup.BABY]: set.babyToddlerImmune,
    [AgeGroup.TODDLER]: set.babyToddlerImmune,
    [AgeGroup.CHILD]: set.childImmune,
    [AgeGroup.ADULT]: set.adultImmune,
  },
  lung: {
    [AgeGroup.BABY]: set.paediatricLungs,
    [AgeGroup.TODDLER]: set.paediatricLungs,
    [AgeGroup.CHILD]: set.paediatricLungs,
    [AgeGroup.ADULT]: set.adultLungs,
  },
});

const BODY_BY_AGE_GROUP: Record<AgeGroup, string> = {
  [AgeGroup.BABY]: babyToddlerBody,
  [AgeGroup.TODDLER]: babyToddlerBody,
  [AgeGroup.CHILD]: childBody,
  [AgeGroup.ADULT]: adultBody,
};

export type OrganArtworkQuery = {
  lookupCode: OrganLookupCode;
  flagLevel: GutBodyConnectionsFlagLevel;
  ageGroup: AgeGroup;
  pregnant: boolean;
  /** `tinyaccount.sex`. Only F draws a uterus; O follows M, as the ticket asks. */
  sex?: string | null;
  /**
   * Every organ code the body was handed. The gut is recut where the liver and
   * kidneys overlap it, so it has to know they are coming before it picks a
   * drawing. Omit it and the gut stays on the MVP artwork.
   */
  drawnCodes?: ReadonlySet<OrganLookupCode>;
  /** Defaults to `high`. */
  quality?: OrganArtworkQuality;
  /** Use this colour's drawing where one exists, ignoring flag level. */
  colorVariant?: GutBodyConnectionsColorVariant;
};

/**
 * One positioned drawing. An organ can own several: `gut` is the stomach and the
 * intestines, which sit in different places but share the organ's flag level.
 * `part` is the CSS positioning key and defaults to the organ's own code.
 */
export type OrganArtworkPart = {
  part: string;
  src: string;
};

type OrganArtworkContext = Omit<
  OrganArtworkQuery,
  'lookupCode' | 'flagLevel' | 'quality' | 'colorVariant'
>;

/**
 * Whether the body is drawing an organ the gut has to make room for. Age is part
 * of it because every such organ is adult-only, which also keeps this from having
 * to ask what resolved — the gut is itself one of the answers.
 */
const isFullOrganSet = ({ ageGroup, drawnCodes }: OrganArtworkContext) =>
  ageGroup === AgeGroup.ADULT &&
  !!(drawnCodes?.has('liver') || drawnCodes?.has('kidney'));

/**
 * Which intestines a body gets: the baby displaces them, and failing that a uterus
 * costs the sigmoid tail. Each of the three is recut again once the liver or a
 * kidney is drawn over it.
 *
 * The baby and uterus cutouts follow whether `reproductive` is drawn, not the kit:
 * an organ with no conditions is dropped from the body, and a cutout with nothing
 * in it reads as a hole in the gut.
 */
const resolveIntestines = (
  set: OrganArtworkSet,
  query: OrganArtworkContext,
): GutBodyConnectionsArtwork => {
  const full = isFullOrganSet(query);
  const drawsReproductive = !!query.drawnCodes?.has('reproductive');

  if (
    query.ageGroup === AgeGroup.ADULT &&
    query.pregnant &&
    drawsReproductive
  ) {
    return full ? set.gutBabyFull : set.gutWithBaby;
  }

  if (!full) return set.gut;
  return query.sex === 'F' && drawsReproductive
    ? set.gutUterusFull
    : set.gutFull;
};

const resolveStomach = (
  set: OrganArtworkSet,
  query: OrganArtworkContext,
): GutBodyConnectionsArtwork =>
  isFullOrganSet(query) ? set.stomachFull : set.stomach;

/** One drawing as the organ's single part, or as its mirrored pair. */
const place = (
  lookupCode: OrganLookupCode,
  src: string,
): OrganArtworkPart[] => {
  const pair = MIRRORED_PAIRS[lookupCode];
  return pair ? pair.map(part => ({ part, src })) : [{ part: lookupCode, src }];
};

/**
 * The flag-level sprite a colour variant reuses when it has no dedicated art.
 * Orange has no sprites of its own yet, and `moderate` is close enough in colour.
 */
const COLOR_VARIANT_FALLBACK: Record<
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsFlagLevel
> = {
  purple: 'low',
  orange: 'moderate',
};

/**
 * The colour-variant drawing where the design provides one, else its fallback
 * flag-level sprite; with no variant, the organ's own flag colour.
 */
export const pickArtwork = (
  artwork: GutBodyConnectionsArtwork,
  flagLevel: GutBodyConnectionsFlagLevel,
  colorVariant?: GutBodyConnectionsColorVariant,
): string => {
  if (!colorVariant) return artwork[flagLevel];
  return artwork[colorVariant] ?? artwork[COLOR_VARIANT_FALLBACK[colorVariant]];
};

/**
 * Resolves one organ to the drawings that represent it. Empty when there is no
 * artwork: `oral` was never drawn, and adult-only organs on a younger body —
 * which includes every organ the design file added after the MVP.
 */
export function getOrganImages(query: OrganArtworkQuery): OrganArtworkPart[] {
  const { lookupCode, flagLevel, ageGroup, colorVariant } = query;
  const set = ARTWORK_SETS[query.quality ?? 'high'];

  const pick = (artwork: GutBodyConnectionsArtwork): string =>
    pickArtwork(artwork, flagLevel, colorVariant);

  if (lookupCode === 'gut') {
    return [
      { part: 'gut', src: pick(resolveIntestines(set, query)) },
      { part: 'stomach', src: pick(resolveStomach(set, query)) },
    ];
  }

  // Adult only, and drawn as a fetus once pregnant: the spec pairs uterus and
  // baby as one body-type organ. Pregnancy alone gates the fetus, matching the
  // intestines-with-baby drawing whose gap it fills.
  if (lookupCode === 'reproductive') {
    if (ageGroup !== AgeGroup.ADULT) return [];
    if (query.pregnant) return [{ part: 'fetus', src: pick(set.fetus) }];
    return query.sex === 'F'
      ? [{ part: 'reproductive', src: pick(set.uterus) }]
      : [];
  }

  const byAgeGroup = organsByAgeGroup(set)[lookupCode];
  if (byAgeGroup) {
    return place(lookupCode, pick(byAgeGroup[ageGroup]));
  }

  const adultOnly = adultOnlyOrgans(set)[lookupCode];
  if (adultOnly) {
    if (ageGroup !== AgeGroup.ADULT) return [];
    return place(lookupCode, pick(adultOnly));
  }

  const invariant = ageInvariantOrgans(set)[lookupCode];
  return invariant ? place(lookupCode, pick(invariant)) : [];
}

const ADULT_BODY_MIN_AGE_YEARS = 13;

/**
 * The silhouette a kit is drawn on, which is not always the age group its organs
 * come from: pregnancy is not a variant of its own, and ages 13-18 take the adult
 * body with their child organs.
 */
export function getBodyAgeGroup({
  age,
  pregnant,
}: {
  age: number;
  pregnant: boolean;
}): AgeGroup {
  return pregnant || getAgeInYears(age) >= ADULT_BODY_MIN_AGE_YEARS
    ? AgeGroup.ADULT
    : getAgeGroup(age);
}

export const getBodyImage = (bodyAgeGroup: AgeGroup): string =>
  BODY_BY_AGE_GROUP[bodyAgeGroup];
